import React from "react";
import { useTranslation } from "react-i18next";
import { formatNumberDe } from "../../../utils/format";
import { buildMilestoneView, type MilestoneNode, type MilestoneView } from "../milestones.model";
import { MilestonesSkeleton } from "./MilestonesSkeleton";
import "./MilestonesCard.css";

interface MilestonesCardProps {
  /** Current total user count (from `adminDashboardKpis`); null while unknown. */
  totalUsers: number | null;
  loading: boolean;
}

/** Flag icon for the card header (colored via CSS `currentColor`). */
const FlagIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 22V9M5 15h9.5a2 2 0 0 0 1.9-1.4l1.8-5.5A1 1 0 0 0 17.2 7H12l1-4H6.5a1.5 1.5 0 0 0-1.5 1.2" />
  </svg>
);

/** White check inside a reached milestone node. */
const CheckIcon: React.FC = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

/**
 * Milestones card (§6.4 FE-4): growth goals since launch — a header summary + a
 * next-milestone progress bar + a horizontal node timeline. Driven purely by
 * `totalUsers` (thresholds from FE config); no dedicated backend query.
 */
export const MilestonesCard: React.FC<MilestonesCardProps> = ({ totalUsers, loading }) => {
  const { t } = useTranslation();

  if (totalUsers == null) {
    return loading ? <MilestonesSkeleton /> : null;
  }

  const view = buildMilestoneView(totalUsers);

  const nodeLabel = (node: MilestoneNode): string =>
    t(node.first ? "dashboard.milestones.firstLabel" : "dashboard.milestones.userLabel", {
      value: formatNumberDe(node.value),
    });

  return (
    <div className="milestones-card">
      <div className="milestones-head">
        <div>
          <div className="milestones-title">
            <FlagIcon />
            {t("dashboard.milestones.title")}
          </div>
          <div className="milestones-subtitle">
            {t("dashboard.milestones.subtitle")} ·{" "}
            <span className="milestones-achieved">
              {t("dashboard.milestones.achieved", { done: view.doneCount, total: view.total })}
            </span>
          </div>
        </div>
        <div className="milestones-next">
          <div className="milestones-next-label">{t("dashboard.milestones.nextTitle")}</div>
          <div className="milestones-next-value">
            {view.allDone ? (
              t("dashboard.milestones.allDone")
            ) : (
              <>
                <strong>{formatNumberDe(view.nextRemaining)}</strong>{" "}
                {t("dashboard.milestones.remainingSuffix", { label: nodeLabel(nextNode(view)) })}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Next-milestone progress bar */}
      <div className="milestones-progress">
        <div className="milestones-progress-labels">
          <span>{t("dashboard.milestones.currentUsers", { value: formatNumberDe(view.currentUsers) })}</span>
          <span>{formatNumberDe(view.nextValue)}</span>
        </div>
        <div className="milestones-progress-track">
          <div className="milestones-progress-fill" style={{ width: `${view.nextPct}%` }} />
        </div>
      </div>

      {/* Timeline */}
      <div className="milestones-timeline">
        {view.nodes.map((node) => (
          <div className={`milestone-node milestone-node--${node.state}`} key={node.value}>
            {node.showConnector && (
              <div className="milestone-connector">
                <div className="milestone-connector-fill" style={{ width: `${node.connectorPct}%` }} />
              </div>
            )}
            <div className="milestone-dot">{node.state === "done" && <CheckIcon />}</div>
            <div className="milestone-value">{formatNumberDe(node.value)}</div>
            <div className="milestone-label">{nodeLabel(node)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

/** The node the "next milestone" callout points at (the in-progress one). */
function nextNode(view: MilestoneView): MilestoneNode {
  return view.nodes.find((n) => n.state === "next") ?? view.nodes[view.nodes.length - 1];
}
